import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';

import styles from './RoleDeletionImpactDialog.module.css';

// Volunteer Role Deletion impact dialog
// (splitfast-portal docs/volunteer-role-deletion-design.md §4, §9).
// Rendered from the 409 impact report returned by DELETE /Volunteers/roles/{id}.
// Same mental model as the program-edit ImpactReviewDialog: EVERYTHING listed
// applies together when the user presses Delete Role; Cancel closes with
// nothing changed. The only interactive element is the acknowledgment checkbox
// on the signups section, which gates Delete until ticked.

const plural = (count, singular, pluralWord) =>
  count === 1 ? singular : pluralWord || `${singular}s`;

const formatMeetDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

const RoleDeletionImpactDialog = ({
  isOpen,
  report,
  onCancel,
  onConfirm,
  deleting,
}) => {
  const [cancellationsAcknowledged, setCancellationsAcknowledged] =
    useState(false);

  // A refreshed report (e.g. after a race-safety 409) resets the acknowledgment
  useEffect(() => {
    setCancellationsAcknowledged(false);
  }, [report]);

  if (!isOpen || !report) return null;

  const affectedMeets = report.affectedMeets || [];
  const requirementsToRemove = report.requirementsToRemove || [];
  const completedUsage = report.completedMeetUsage || {};
  const openSignups = affectedMeets.flatMap((meet) => meet.openSignups || []);

  const canDelete = openSignups.length === 0 || cancellationsAcknowledged;

  const handleConfirm = () => {
    if (!canDelete || deleting) return;
    onConfirm({
      cancelSignupIds: openSignups.map((signup) => signup.volunteerSignupId),
    });
  };

  return (
    <CustomNewConfirm
      isOpen={isOpen}
      onClose={onCancel}
      onCancel={onCancel}
      title={`Delete Volunteer Role — ${report.roleName}`}
      description="This role is still in use on meets that haven't been completed. Everything listed below will be applied together when you select Delete Role. Nothing changes if you cancel."
      confirmButtonText={deleting ? 'Deleting...' : 'Delete Role'}
      confirmButtonVariant="danger"
      onConfirm={handleConfirm}
      isLoading={deleting || !canDelete}
    >
      <div className={styles.sections}>
        {affectedMeets.length > 0 && (
          <div className={styles.section}>
            <div className={`${styles.sectionTitle} ${styles.action}`}>
              Signups to be cancelled
              <span className={styles.count}>
                {openSignups.length} {plural(openSignups.length, 'signup')}
              </span>
            </div>
            {affectedMeets.map((meet) => (
              <div key={meet.meetId} className={styles.meetGroup}>
                <div className={styles.meetName}>
                  {meet.meetName} — {formatMeetDate(meet.meetDate)}
                </div>
                <ul className={styles.itemList}>
                  {(meet.openSignups || []).map((signup) => (
                    <li key={signup.volunteerSignupId}>
                      {signup.memberName}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <label className={styles.ackRow}>
              <input
                type="checkbox"
                className={styles.ackCheckbox}
                checked={cancellationsAcknowledged}
                onChange={(e) => setCancellationsAcknowledged(e.target.checked)}
              />
              <span>
                Cancel{' '}
                {openSignups.length === 1
                  ? 'this signup'
                  : `these ${openSignups.length} signups`}{' '}
                when I delete the role
              </span>
            </label>
            {!cancellationsAcknowledged && (
              <div className={styles.ackHint}>
                Delete Role stays disabled until you confirm these
                cancellations. Cancelled members can sign up for another role
                while spots remain.
              </div>
            )}
          </div>
        )}

        {requirementsToRemove.length > 0 && (
          <div className={styles.section}>
            <div className={`${styles.sectionTitle} ${styles.info}`}>
              Removed from these meets — no signups
              <span className={styles.count}>
                {requirementsToRemove.length}{' '}
                {plural(requirementsToRemove.length, 'meet')}
              </span>
            </div>
            <ul className={styles.itemList}>
              {requirementsToRemove.map((meet) => (
                <li key={meet.meetId}>
                  {meet.meetName} — {formatMeetDate(meet.meetDate)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {completedUsage.meetCount > 0 && (
          <div className={styles.section}>
            <div className={`${styles.sectionTitle} ${styles.info}`}>
              Completed meets — history kept
            </div>
            <div className={styles.historyNote}>
              This role was used in {completedUsage.meetCount}{' '}
              {plural(completedUsage.meetCount, 'completed meet')}
              {completedUsage.completedSignupCount > 0 && (
                <>
                  {' '}
                  with {completedUsage.completedSignupCount} completed{' '}
                  {plural(completedUsage.completedSignupCount, 'signup')}
                </>
              )}
              . Awarded points and hours are not affected.
            </div>
          </div>
        )}
      </div>
    </CustomNewConfirm>
  );
};

RoleDeletionImpactDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  report: PropTypes.shape({
    roleId: PropTypes.string,
    roleName: PropTypes.string,
    affectedMeets: PropTypes.array,
    requirementsToRemove: PropTypes.array,
    completedMeetUsage: PropTypes.shape({
      meetCount: PropTypes.number,
      completedSignupCount: PropTypes.number,
    }),
  }),
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  deleting: PropTypes.bool,
};

export default RoleDeletionImpactDialog;
